import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddAccessTokenDialog from '../dialogs/AddAccessTokenDialog/AddAccessTokenDialog';
import { AddButton } from '../ui/inputs/AddButton/AddButton';
import styled from 'styled-components';
import DateTimeWithOffset from '../DateTimeWithOffset/DateTimeWithOffset';
import TrashCan from '../../icons/TrashCan';
import { deleteAPITokenCall, getAPITokensCall } from '../../store/auth/sagas';
import Loading from '../Loading/Loading';
import moment from 'moment';
import { Link } from '../ui';
import { baseURL } from '../../store/root-saga';

export interface AccessToken {
  id: string;
  name: string;
  value: string;
  created_at: string;
  expires_at: string;
  last_used_at: string | null;
}

interface Props {}

const AccessTokenManager: React.FC<Props> = ({}) => {
  const { t, i18n } = useTranslation();
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    getAPITokensCall()
      .then((result) => {
        switch (result.type) {
          case 'ok':
            setTokens(result.value.data);
            break;
          case 'validation-error':
          case 'error':
            break;
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const deleteToken = (id: string) => {
    // API call
    setIsLoading(true);
    deleteAPITokenCall(id)
      .then((result) => {
        switch (result.type) {
          case 'ok':
            setTokens(tokens.filter((token) => token.id != id));
            break;
          case 'validation-error':
          case 'error':
            break;
        }
      })
      .finally(() => setIsLoading(false));
  };

  const docsURL = `${baseURL}/docs/api`;

  return (
    <div className="flex justify-center">
      <StyledDiv>
        <StyledMessageWrapper className="flex flex-col justify-center items-center">
          <h2 className="text-xl font-bold w-max">{t('Integrate with our Public API')}</h2>
          <Link href={docsURL} target="_blank" className="text-sm mt-2">
            {t('View API documentation')}
          </Link>
          <p className="mt-4 mb-6">
            {t(
              'Access tokens allow you to authenticate and interact with our public API securely —without needing to share your password. Each token acts like a unique key tied to your account and can be used in scripts, integrations, or other external tools.',
            )}
          </p>
        </StyledMessageWrapper>
        <section className="bg-white p-6 rounded-lg shadow-md w-full">
          <div className="flex items-center gap-x-4 mb-4">
            <p className="text-xl font-medium">{t('Access tokens')}</p>
            <AddButton
              label={t('Add token')}
              onClick={() =>
                setDialog(
                  <AddAccessTokenDialog
                    handleClose={() => setDialog(undefined)}
                    handleOk={(token) => setTokens([...tokens, token])}
                  />,
                )
              }
            />
          </div>
          {isLoading ? (
            <Loading compact />
          ) : (
            <div className="overflow-x-auto">
              {tokens.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Name')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Created at')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Expires at')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Last used at')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('Actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tokens
                      .sort((a, b) => {
                        // @ts-ignore
                        return new Date(a.created_at) - new Date(b.created_at);
                      })
                      .map((token, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 text-sm text-gray-700">{token.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <DateTimeWithOffset date={token.created_at} showTime />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <DateTimeWithOffset date={token.expires_at} showTime />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {token.last_used_at ? (
                              <span>{moment.utc(token.last_used_at).fromNow()}</span>
                            ) : (
                              <span>{t('Not used')}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="cursor-pointer flex items-center" onClick={() => deleteToken(token.id)}>
                              <TrashCan />
                              <span className="ml-1">{t('Delete')}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  <p>{t('No access tokens have been created yet.')}</p>
                </div>
              )}
            </div>
          )}
        </section>
      </StyledDiv>
      {dialog}
    </div>
  );
};

const StyledDiv = styled.div`
  width: 48rem;
`;

const StyledMessageWrapper = styled.div`
  h2 {
    font-size: 1.45rem;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.5rem;
  }
`;

export default AccessTokenManager;
